if ( typeof ProductItemAvailability !== 'function' ) {

  class ProductItemAvailability extends HTMLElement {

    connectedCallback() {
      this._productId = parseInt(this.dataset.productId, 10);
      this._check();
      document.addEventListener('deliveryConfigSaved', () => this._check());
    }

    async _check() {
      const config = this._loadConfig();

      if ( !config?.locationId || !config?.date ) {
        this.innerHTML = '';
        return;
      }

      const filter = this._sharedFilter();
      if ( !filter ) return;

      const dateIso = this._resolveDate(config.date);

      try {
        const data = await filter.getProductAvailability(
          this._productId, config.locationId, dateIso
        );
        this._render(data, config.date);
      } catch (_) {
        this.innerHTML = '';
      }
    }

    _render(data, date) {
      const isToday    = date === 'today';
      const isTomorrow = date === 'tomorrow';

      if ( isToday || isTomorrow ) {
        const dayLabel   = isToday ? 'today' : 'tomorrow';
        const otherLabel = isToday ? 'tomorrow' : 'today';
        const avail      = isToday ? data.is_available_today    : data.is_available_tomorrow;
        const availOther = isToday ? data.is_available_tomorrow : data.is_available_today;

        if ( avail ) {
          this.innerHTML = `<span class="alert alert--unstyled alert--circle alert--success">Available ${dayLabel}</span>`;
        } else if ( availOther ) {
          this.innerHTML = `<span class="alert alert--unstyled alert--circle alert--warning">Sold out ${dayLabel}. Reserve for ${otherLabel}</span>`;
        } else {
          this.innerHTML = `<span class="alert alert--unstyled alert--circle alert--warning">Sold out</span>`;
        }
      } else {
        // Specific ISO date — fall back to generic available/unavailable
        const qty       = data.available_quantity ?? 0;
        const available = data.available && qty > 0;
        if ( !available ) {
          this.innerHTML = `<span class="alert alert--unstyled alert--circle alert--warning">Sold Out</span>`;
        } else {
          this.innerHTML = `<span class="alert alert--unstyled alert--circle alert--success">Available</span>`;
        }
      }
    }

    _resolveDate(date) {
      const toMiami = (offsetDays) => {
        const now = new Date();
        const m   = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
        m.setDate(m.getDate() + offsetDays);
        const p = (n) => String(n).padStart(2, '0');
        return `${m.getFullYear()}-${p(m.getMonth() + 1)}-${p(m.getDate())}`;
      };
      if ( date === 'today' )    return toMiami(0);
      if ( date === 'tomorrow' ) return toMiami(1);
      return date;
    }

    // Shared instance so the cache is reused across all cards on the page
    _sharedFilter() {
      if ( !window.InventoryFilter ) return null;
      if ( !window._sharedInventoryFilter ) {
        window._sharedInventoryFilter = new window.InventoryFilter();
      }
      return window._sharedInventoryFilter;
    }

    _loadConfig() {
      try {
        return JSON.parse(localStorage.getItem('domaselo_delivery_config'));
      } catch (_) {
        return null;
      }
    }

  }

  if ( typeof customElements.get('product-item-availability') === 'undefined' ) {
    customElements.define('product-item-availability', ProductItemAvailability);
  }

}

if ( typeof ComplementaryProductAvailability !== 'function' ) {

  class ComplementaryProductAvailability extends HTMLElement {

    connectedCallback() {
      this._productId = parseInt(this.dataset.productId, 10);
      this._buyEl = this.querySelector('quick-add-to-cart, quick-view-product');
      this._check();
      document.addEventListener('deliveryConfigSaved', () => this._check());
    }

    async _check() {
      const config = this._loadConfig();

      if ( !config?.locationId || !config?.date ) {
        this._setBuyButton(true);
        return;
      }

      const filter = this._sharedFilter();
      if ( !filter ) return;

      const dateIso = this._resolveDate(config.date);

      try {
        const data = await filter.getProductAvailability(this._productId, config.locationId, dateIso);
        this._applyAvailability(data, config.date);
      } catch (_) {
        this._setBuyButton(true);
      }
    }

    _applyAvailability(data, date) {
      const isToday    = date === 'today';
      const isTomorrow = date === 'tomorrow';

      let available;
      if ( isToday || isTomorrow ) {
        available = isToday ? data.is_available_today : data.is_available_tomorrow;
      } else {
        available = data.available && (data.available_quantity ?? 0) > 0;
      }

      this._setBuyButton(available);
    }

    _setBuyButton(available) {
      if ( !this._buyEl ) return;

      if ( available ) {
        this._buyEl.hidden = false;
        const soldOut = this.querySelector('[data-complementary-sold-out]');
        if ( soldOut ) soldOut.remove();
      } else {
        this._buyEl.hidden = true;
        if ( !this.querySelector('[data-complementary-sold-out]') ) {
          const btn = document.createElement('button');
          btn.setAttribute('data-complementary-sold-out', '');
          btn.className = `button button--${window.KROWN?.settings?.product_card_button_style || 'secondary'} button--small`;
          btn.disabled = true;
          btn.style.cssText = 'pointer-events: none; opacity: 0.36;';
          btn.innerHTML = `<span class="button__text">${window.KROWN?.settings?.locales?.availability_sold_out || 'Sold out'}</span>`;
          this.appendChild(btn);
        }
      }
    }

    _resolveDate(date) {
      const toMiami = (offsetDays) => {
        const now = new Date();
        const m   = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
        m.setDate(m.getDate() + offsetDays);
        const p = (n) => String(n).padStart(2, '0');
        return `${m.getFullYear()}-${p(m.getMonth() + 1)}-${p(m.getDate())}`;
      };
      if ( date === 'today' )    return toMiami(0);
      if ( date === 'tomorrow' ) return toMiami(1);
      return date;
    }

    _sharedFilter() {
      if ( !window.InventoryFilter ) return null;
      if ( !window._sharedInventoryFilter ) {
        window._sharedInventoryFilter = new window.InventoryFilter();
      }
      return window._sharedInventoryFilter;
    }

    _loadConfig() {
      try {
        return JSON.parse(localStorage.getItem('domaselo_delivery_config'));
      } catch (_) {
        return null;
      }
    }

  }

  if ( typeof customElements.get('complementary-product-availability') === 'undefined' ) {
    customElements.define('complementary-product-availability', ComplementaryProductAvailability);
  }

}